<template>
  <!-- eslint-disable max-len -->
  <b-card>
    <b-card-title>
      Audit Trail
    </b-card-title>

    <section class="audit-trail-card">
      <div class="audit-trail-table mt-1">
        <b-table
          hover
          :fields="columns"
          :items="callEvents"
          :busy="loading"
          empty-text="No Audio found"
          primary-key="date"
        >
          <template v-slot:table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>

          <template #cell(image)="data">
            <button
              v-if="data.item.image !== null"
              class="btn btn-sm btn-primary"
              @click="openImage(data.item.image)"
            >
              <span class="far fa-sm fa-images"></span>
            </button>
          </template>
        </b-table>
      </div>
      <b-row v-if="callEvents.length > 0">
        <b-col md="6" class="my-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </section>
  </b-card>
  <!-- eslint-enable -->
</template>

<style>
  .call-event-image {
    max-width: 100%;
    max-height: 90vh;
  }
</style>

<script>
  import moment from 'moment';
  import { logger } from '@/util/logger';
  import { CALL_EVENT_ACTION_SUB_TYPES, CALL_EVENT_EVENT_SUB_TYPES } from '@/util/callEvent.subTypes';
  import FullScreenImageModal from '../../components/modals/FullScreenImageModal';
  import VisitService from '@/services/VisitService';

  export default {
    name: 'audit-trail',
    props: {
      visitId: {
        type: String,
        required: true,
      },
      otp: {
        type: String,
        required: true,
      },
    },
    watch: {
      currentPage() {
        this.getCallEvents();
      },
    },
    data() {
      return {
        loading: true,
        columns: [
          {
            key: 'date',
            label: 'Date',
          },
          {
            key: 'time',
            label: 'Time',
          },
          {
            key: 'event',
            label: 'Event',
          },
          {
            key: 'eventDescription',
            label: 'Description',
          },
          {
            key: 'image',
            label: 'Images',
          },
        ],
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        totalPages: 0,
        callEvents: [],
      };
    },
    mounted() {
      this.getCallEvents();
    },
    methods: {
      getCallEvents() {
        this.loading = true;
        const skip = (this.currentPage - 1) * this.perPage;
        this.callEvents = [];

        VisitService.getCallEvents(this.visitId, this.otp, skip, this.perPage).then(res => {
          this.callEvents = res.data.data;
          if(this.callEvents && this.callEvents.length > 0) {
            this.callEvents = this.callEvents.sort((c1, c2) => {
              const c1OccurredOn = new Date(c1.occurred_on || c1.created_at);
              const c2OccurredOn = new Date(c2.occurred_on || c2.created_at);
              return c1OccurredOn.getTime() - c2OccurredOn.getTime();
            }).filter((callEvent) => {
              return callEvent.sub_type !== CALL_EVENT_EVENT_SUB_TYPES.CALL_SECURITY_JOINED;
            }).map((callEvent) => {
              let rowVariant = '';
              if([CALL_EVENT_ACTION_SUB_TYPES.RESUMED_INVALID_FACE, CALL_EVENT_ACTION_SUB_TYPES.RESUMED_MT].includes(callEvent.sub_type)) {
                rowVariant = 'success';
              }
              // eslint-disable-next-line max-len
              if ([CALL_EVENT_ACTION_SUB_TYPES.PAUSED_MT,
                CALL_EVENT_ACTION_SUB_TYPES.PAUSED_INVALID_FACE,
                CALL_EVENT_EVENT_SUB_TYPES.ADULT_CONTENT_ALERT,
                CALL_EVENT_EVENT_SUB_TYPES.INVALID_FACE_ALERT,
                CALL_EVENT_EVENT_SUB_TYPES.SOFT_PAUSE_INVALID_FACE,
                CALL_EVENT_EVENT_SUB_TYPES.SOFT_PAUSE_ADULT_CONTENT,
                CALL_EVENT_EVENT_SUB_TYPES.SOFT_PAUSE_TOO_MANY_FACES,
                CALL_EVENT_EVENT_SUB_TYPES.SOFT_PAUSE_TOO_FEW_FACES,
                CALL_EVENT_EVENT_SUB_TYPES.SOFT_PAUSE_RESUMED,
                CALL_EVENT_EVENT_SUB_TYPES.PRISONER_SOFT_PAUSE_INVALID_FACE,
                CALL_EVENT_EVENT_SUB_TYPES.PRISONER_SOFT_PAUSE_ADULT_CONTENT,
                CALL_EVENT_EVENT_SUB_TYPES.PRISONER_SOFT_PAUSE_TOO_MANY_FACES,
                CALL_EVENT_EVENT_SUB_TYPES.PRISONER_SOFT_PAUSE_TOO_FEW_FACES,
                CALL_EVENT_EVENT_SUB_TYPES.PRISONER_SOFT_PAUSE_RESUMED].includes(callEvent.sub_type)) {
                rowVariant = 'warning';
              }
              if(callEvent.sub_type === CALL_EVENT_ACTION_SUB_TYPES.TERMINATED) {
                rowVariant = 'danger';
              }
              if([CALL_EVENT_EVENT_SUB_TYPES.IDENTITY_CHECK, CALL_EVENT_EVENT_SUB_TYPES.PRE_CALL_SECURITY, CALL_EVENT_EVENT_SUB_TYPES.ADULT_CONTENT_CHECK].includes(callEvent.sub_type)) {
                if(callEvent.confidence_check_status === 'Passed') {
                  rowVariant = 'success';
                } else if(callEvent.confidence_check_status === 'Failed'){
                  rowVariant = 'warning';
                }
              }
              return {
                date: moment(callEvent.occurred_on || callEvent.created_at).format('DD/MM/YYYY'),
                time: moment(callEvent.occurred_on || callEvent.created_at).format('HH:mm:ss'),
                event: this.getEventDescriptionAndName(callEvent).name,
                eventDescription: this.getEventDescriptionAndName(callEvent).description,
                image: callEvent.image_url || null,
                _rowVariant: rowVariant,
              };
            });
          }
          this.totalRows = res.data.count;
          this.totalPages = Math.ceil(this.totalRows / this.perPage);
        }).catch(err => {
          // OTP issue
          if(err.response && err.response.status === 403) {
            // Let the parent know something went wrong with the OTP
            this.$parent.$emit('otpIssue');
          } else if(err.response && err.response.status === 401) {
            logger.error('Error getting call events', err, null, 'AuditTrail:getCallEvents');
            this.$toast.error('The Access code provided is Invalid or Expired. Please refresh the page and try again', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          } else {
            logger.error('Error getting call events', err, null, 'AuditTrail:getCallEvents');
            this.$toast.error('Error getting call events. Please refresh the page and try again', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          }
        }).finally(() => {
          this.loading = false;
        });
      },
      selectPage(newPage) {
        this.currentPage = newPage;
      },
      getEventDescriptionAndName(callEvent) {
        switch(callEvent.sub_type) {
          case CALL_EVENT_ACTION_SUB_TYPES.TERMINATED:
            return this.getTerminatedDescriptionAndName(callEvent);
          case CALL_EVENT_ACTION_SUB_TYPES.PAUSED_MT:
            return this.getPausedMtDescriptionAndName();
          case CALL_EVENT_ACTION_SUB_TYPES.RESUMED_MT:
            return this.getResumedMtDescriptionAndName();
          case CALL_EVENT_ACTION_SUB_TYPES.PAUSED_INVALID_FACE:
            return this.getPausedInvalidFaceDescriptionAndName(callEvent);
          case CALL_EVENT_ACTION_SUB_TYPES.RESUMED_INVALID_FACE:
            return this.getResumedInvalidFaceDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.FACE_LEFT:
            return this.getFaceLeftDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.FACE_ENTERED:
            return this.getFaceEnteredDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.TOO_MANY_FACES:
            return this.getTooManyFacesDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.IDENTITY_CHECK:
            return this.getIdentityCheckDescriptionAndName(callEvent);
          case CALL_EVENT_EVENT_SUB_TYPES.ADULT_CONTENT_CHECK:
            return this.getAdultContentCheckDescriptionAndName(callEvent);
          case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_JOINED:
            return this.getPrisonerJoinedDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_LEFT:
            return this.getPrisonerLeftDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_JOINED:
            return this.getVisitorJoinedDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_LEFT:
            return this.getVisitorLeftDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.PRE_CALL_SECURITY:
            return this.getPreCallSecurityDescriptionAndName('Visitor', callEvent);
          case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_PRE_CALL_SECURITY:
            return this.getPreCallSecurityDescriptionAndName('Prisoner', callEvent);
          case CALL_EVENT_EVENT_SUB_TYPES.MASTER_TERMINAL_MONITOR_START:
            return this.getMasterTerminalMonitorStartDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.MASTER_TERMINAL_MONITOR_END:
            return this.getMasterTerminalMonitorEndDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.MASTER_TERMINAL_NOTE:
            return this.getMasterTerminalNoteDescriptionAndName(callEvent);
          case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_CAMERA_FLIP:
            return this.getVisitorCameraFlippedDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_SCREEN_SHOT:
            return this.getVisitorScreenShotDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_SCREEN_RECORDING:
            return this.getVisitorScreenRecordingDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.ADULT_CONTENT_ALERT:
          case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_ADULT_CONTENT_ALERT:
            return this.getAdultContentAlertDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.INVALID_FACE_ALERT:
          case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_INVALID_FACE_ALERT:
            return this.getInvalidFaceAlertDescriptionAndName();
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_DISMISSED_INVALID_FACE_PAUSE:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_DISMISSED_ADULT_CONTENT_PAUSE:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_DISMISSED_ADULT_CONTENT_ALERT:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_DISMISSED_INVALID_FACE_ALERT:
            return {
              name: callEvent.sub_type.replace(/_/g, ' '),
              description: 'The staff member has dismissed the banner',
            };
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_SHOWN_INVALID_FACE_PAUSE:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_SHOWN_ADULT_CONTENT_PAUSE:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_SHOWN_ADULT_CONTENT_ALERT:
          case CALL_EVENT_EVENT_SUB_TYPES.BANNER_SHOWN_INVALID_FACE_ALERT:
            return {
              name: callEvent.sub_type.replace(/_/g, ' '),
              description: 'The banner has been shown to the staff member',
            };
          case CALL_EVENT_EVENT_SUB_TYPES.NEW_CHAT_MESSAGE:
            return {
              name: callEvent.sub_type.replace(/_/g, ' '),
              description: `${callEvent.meta.name} said: ${callEvent.meta.message}`,
            };
          default:
            return {
              name: callEvent.sub_type.replace(/_/g, ' '),
              description: '-',
            };
        }
      },
      getVisitorScreenRecordingDescriptionAndName() {
        return {
          name: 'Visitor screen recording',
          description: 'Screen recording detected and call suspended',
        };
      },
      getTerminatedDescriptionAndName(callEvent) {
        let description = '-';
        if(callEvent.reason) {
          description = callEvent.reason;
        }
        return {
          name: 'Terminated',
          description: description,
        };
      },
      getPausedMtDescriptionAndName() {
        let description = 'Call paused so staff can speak with resident';
        return {
          name: 'Master Terminal Pause',
          description: description,
        };
      },
      getResumedMtDescriptionAndName() {
        let description = 'Call resumed';
        return {
          name: 'Master Terminal Resume',
          description: description,
        };
      },
      getPausedInvalidFaceDescriptionAndName(callEvent) {
        let description = 'Paused due to an invalid face being detected';
        if(callEvent.reason) {
          description = callEvent.reason;
        }
        return {
          name: 'Invalid Face',
          description: description,
        };
      },
      getResumedInvalidFaceDescriptionAndName() {
        let description = 'The call has been resumed';
        return {
          name: 'Resume',
          description: description,
        };
      },
      getFaceLeftDescriptionAndName() {
        let description = 'Unable to detect the visitors face';
        return {
          name: 'Face Left',
          description: description,
        };
      },
      getFaceEnteredDescriptionAndName() {
        let description = 'Able to detect visitors face';
        return {
          name: 'Face Detected',
          description: description,
        };
      },
      getTooManyFacesDescriptionAndName() {
        let description = 'More faces than permitted have been detected';
        return {
          name: 'Too Many Faces Detected',
          description: description,
        };
      },
      getIdentityCheckDescriptionAndName(callEvent) {
        let description = '';
        if(callEvent.confidence_check_status === 'Invalid') {
          description = 'Failed as unable to detect a face';
        } else {
          description = `Visitor ${callEvent.confidence_check_status.toLowerCase()} identity check`;
          if(callEvent.confidence_rating) {
            description += ` with a confidence of ${callEvent.confidence_rating.toFixed(2)}%`;
          }
        }
        return {
          name: 'Identity Check',
          description: description,
        };
      },
      getAdultContentCheckDescriptionAndName(callEvent) {
        let description = '';
        if(callEvent.confidence_check_status === 'Invalid') {
          description = 'Failed as no content could be detected';
        } else {
          description = `Visitor ${callEvent.confidence_check_status.toLowerCase()} adult content check`;
          if(callEvent.confidence_rating) {
            description += ` with a confidence of ${callEvent.confidence_rating.toFixed(2)}%`;
          }
        }
        return {
          name: 'Adult Content Check',
          description: description,
        };
      },
      getPrisonerJoinedDescriptionAndName() {
        let description = '-';
        return {
          name: 'Resident Joined',
          description: description,
        };
      },
      getPrisonerLeftDescriptionAndName() {
        let description = '-';
        return {
          name: 'Resident Left',
          description: description,
        };
      },
      getVisitorJoinedDescriptionAndName() {
        let description = '-';
        return {
          name: 'Visitor Joined',
          description: description,
        };
      },
      getVisitorLeftDescriptionAndName() {
        let description = '-';
        return {
          name: 'Visitor Left',
          description: description,
        };
      },
      getPreCallSecurityDescriptionAndName(participant, callEvent) {
        let description = '';
        if(callEvent.confidence_check_status === 'Invalid') {
          description = 'Failed as unable to detect a face';
        } else {
          description = `${participant} ${callEvent.confidence_check_status.toLowerCase()} pre-call identity check`;
          if(callEvent.confidence_rating) {
            description += ` with a confidence of ${callEvent.confidence_rating.toFixed(2)}%`;
          }
        }
        return {
          name: `${participant} Pre-call Identity Check`,
          description: description,
        };
      },
      getMasterTerminalMonitorStartDescriptionAndName() {
        let description = 'Master terminal has begun monitoring the call in real-time';
        return {
          name: 'Monitoring',
          description: description,
        };
      },
      getMasterTerminalMonitorEndDescriptionAndName() {
        let description = 'Master terminal has stopped monitoring the call in real-time';
        return {
          name: 'Stopped Monitoring',
          description: description,
        };
      },
      getMasterTerminalNoteDescriptionAndName(callEvent) {
        let description = '-';
        if(callEvent.reason) {
          description = callEvent.reason;
        }
        return {
          name: 'Note',
          description: description,
        };
      },
      getVisitorCameraFlippedDescriptionAndName() {
        return {
          name: 'Visitor Flipped their camera',
          description: '-',
        };
      },
      getVisitorScreenShotDescriptionAndName() {
        return {
          name: 'Visitor took a screen shot',
          description: 'A warning was given to both the resident and visitor',
        };
      },
      getCallSecurityJoinedDescriptionAndName() {
        return {
          name: 'Automated call security joined',
          description: '-',
        };
      },
      getAdultContentAlertDescriptionAndName() {
        return {
          name: 'Adult Content Alert',
          description: 'The master terminal has been notified that adult content has been detected',
        };
      },
      getInvalidFaceAlertDescriptionAndName() {
        return {
          name: 'Invalid Face Alert',
          description: 'The master terminal has been notified that an invalid face has been detected',
        };
      },
      openImage(imageUrl) {
        this.$modal.show(FullScreenImageModal, {
          imageUrl,
        }, {
          height: 'auto',
          classes: 'max-height-modal',
          scrollable: true,
        });
      },
    },
  };
</script>
